import Icon from "@components/Atoms/Icon";
import parseStorageUrl from "@core/utils/parseStorageUrl";
import PageDataContext from "@core-ui/ContextServices/PageDataContext";
import { useDebounce } from "@core-ui/hooks/useDebounce";
import { useSetFooterButton } from "@core-ui/hooks/useFooterPortal";
import GitlabSourceAPI from "@ext/git/actions/Source/GitLab/logic/GitlabSourceAPI";
import type GitlabSourceData from "@ext/git/actions/Source/GitLab/logic/GitlabSourceData";
import handleFormApiError from "@ext/git/actions/Source/logic/handleApiError";
import validateToken from "@ext/git/actions/Source/logic/validateToken";
import t from "@ext/localization/locale/translate";
import SourceType from "@ext/storage/logic/SourceDataProvider/model/SourceType";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@ui-kit/Button";
import { Retry } from "@ui-kit/ErrorState";
import {
	Form,
	FormDivider,
	FormField,
	FormFieldSet,
	FormSectionHeaderButton,
	FormSectionTitle,
	FormStack,
} from "@ui-kit/Form";
import { Input, SecretInput, TextInput } from "@ui-kit/Input";
import { Skeleton } from "@ui-kit/Skeleton";
import { Tooltip, TooltipContent, TooltipLinkButton, TooltipTrigger } from "@ui-kit/Tooltip";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface EditGitLabProps {
	data?: Partial<GitlabSourceData>;
	onSubmit: (data: GitlabSourceData) => void;
}

const getFormSchema = () =>
	z.object({
		url: z.string({ message: t("must-be-not-empty") }),
		authorName: z.string({ message: t("must-be-not-empty") }),
		authorEmail: z.string({ message: t("must-be-not-empty") }),
		token: z.string({ message: t("must-be-not-empty") }).refine((val) => validateToken(val), {
			message: `${t("invalid2")} ${t("token")}`,
		}),
	});

const getGitlabSettingsUrl = (domain: string) => {
	const hasProtocol = domain.includes("://");
	return `${hasProtocol ? domain : `https://${domain}`}/-/user_settings/personal_access_tokens`;
};

const EditGitLab = ({ onSubmit, data }: EditGitLabProps) => {
	const authServiceUrl = PageDataContext.value.conf.authServiceUrl;
	const { setPrimaryButton } = useSetFooterButton();
	const [isLoading, setIsLoading] = useState(false);

	const form = useForm({
		resolver: zodResolver(getFormSchema()),
		defaultValues: data
			? {
					url: `${data?.protocol ? data.protocol : "https"}://${data?.domain}`,
					token: data?.token,
					authorName: data?.userName,
					authorEmail: data?.userEmail,
				}
			: undefined,
		mode: "onChange",
	});

	const url = form.watch("url");

	const { start: startCheck, cancel: cancelCheck } = useDebounce(
		async (data: GitlabSourceData, domain: string) => {
			if (data.token && domain) {
				const api = new GitlabSourceAPI(data, authServiceUrl, (error) => {
					handleFormApiError(error, form);
					setIsLoading(false);
				});

				const user = await api.getUser();
				form.setValue("authorName", user.name, { shouldDirty: true });
				form.setValue("authorEmail", user.email, { shouldDirty: true });
				setIsLoading(false);
			}
		},
		500,
		true,
	);

	useEffect(() => {
		if (data?.isInvalid) form.setError("token", { type: "invalid", message: `${t("invalid2")} ${t("token")}` });
	}, [data?.isInvalid, form.setError]);

	const onChangeAuthFields = useCallback(() => {
		const formValues = form.getValues();

		if (!formValues.token || !formValues.url) {
			cancelCheck();
			setIsLoading(false);
			return;
		}

		setIsLoading(true);
		const hasProtocol = formValues.url.includes("://");
		const { domain, protocol, origin, pathname } = parseStorageUrl(
			hasProtocol ? formValues.url : `https://${formValues.url}`,
		);
		startCheck(
			{
				url: formValues.url,
				token: formValues.token,
				sourceType: SourceType.gitLab,
				domain,
				userName: "",
				userEmail: "",
				protocol,
				origin,
				pathname,
			} as GitlabSourceData,
			domain,
		);
	}, [form, cancelCheck, startCheck]);

	const formSubmit = useCallback(
		(e) => {
			form.handleSubmit((formData) => {
				const hasProtocol = formData.url.includes("://");
				const { domain, protocol } = parseStorageUrl(hasProtocol ? formData.url : `https://${formData.url}`);
				const isInvalid = !!form.formState.errors.token?.message || !!form.formState.errors.url?.message;

				onSubmit({
					sourceType: SourceType.gitLab,
					token: formData.token,
					userName: formData.authorName,
					userEmail: formData.authorEmail,
					domain,
					protocol,
					createDate: new Date().toJSON(),
					isInvalid,
				});
			})(e);
		},
		[form, onSubmit],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	useLayoutEffect(() => {
		const primaryButton = (
			<Button onClick={formSubmit} type="submit">
				{t("add")}
			</Button>
		);

		setPrimaryButton(primaryButton);

		return () => {
			setPrimaryButton(null);
		};
	}, []);

	return (
		<Form asChild {...form}>
			<form className="contents ui-kit" onSubmit={formSubmit}>
				<FormStack>
					<FormField
						control={({ field }) => (
							<Input
								{...field}
								data-qa="qa-gitlab-url"
								onChange={(value) => {
									field.onChange(value);
									onChangeAuthFields();
								}}
								placeholder={t("forms.gitlab-source-data.props.url.placeholder")}
								readOnly={!!data?.domain}
							/>
						)}
						description={t("forms.gitlab-source-data.props.url.description")}
						layout="vertical"
						name="url"
						readonly={!!data?.domain}
						title={t("forms.gitlab-source-data.props.url.name")}
					/>

					<FormDivider />

					<div className="flex flex-row items-center justify-between gap-3 pb-2 lg:gap-4">
						<FormSectionTitle>{t("authorization")}</FormSectionTitle>
						<FormSectionHeaderButton size="xs" type="button" variant="link">
							<a
								href={getGitlabSettingsUrl(url || "https://gitlab.com")}
								rel="noopener noreferrer"
								target="_blank"
							>
								{`${t("create")} ${t("token")}`}
							</a>
							<Tooltip>
								<TooltipTrigger asChild>
									<Icon code="info" />
								</TooltipTrigger>
								<TooltipContent>
									<span
										dangerouslySetInnerHTML={{
											// biome-ignore lint/style/useNamingConvention: expected
											__html: t("git.source.gitlab.info"),
										}}
									/>
									<br />
									<a
										href="https://docs.gitlab.com/ee/user/profile/personal_access_tokens.html"
										rel="noopener"
										target="_blank"
									>
										<TooltipLinkButton size="xs">{t("more")}</TooltipLinkButton>
									</a>
								</TooltipContent>
							</Tooltip>
						</FormSectionHeaderButton>
					</div>

					<FormFieldSet style={{ marginTop: 0 }}>
						<FormField
							control={({ field }) => (
								<SecretInput
									{...field}
									data-qa="qa-gitlab-token"
									onChange={(value) => {
										field.onChange(value);
										onChangeAuthFields();
									}}
									placeholder={t("forms.gitlab-source-data.props.token.placeholder")}
									showClearIcon
								/>
							)}
							description={t("forms.gitlab-source-data.props.token.description")}
							layout="vertical"
							name="token"
							title={t("forms.gitlab-source-data.props.token.name")}
						/>
						{form.formState.errors?.token?.type === "invalid" && (
							<div className="flex" style={{ marginTop: "-1.5em" }}>
								<Retry
									className="p-0 ml-auto"
									onClick={() => {
										onChangeAuthFields();
										form.clearErrors("token");
										form.clearErrors("url");
									}}
									startIcon="rotate-ccw"
									type="button"
								>
									{t("try-again")}
								</Retry>
							</div>
						)}

						<FormField
							control={({ field }) =>
								isLoading ? (
									<Skeleton className="w-full" style={{ height: "36px" }} />
								) : (
									<TextInput
										{...field}
										placeholder={
											isLoading
												? t("loading")
												: t("forms.gitlab-source-data.props.userName.placeholder")
										}
									/>
								)
							}
							description={t("forms.gitlab-source-data.props.userName.description")}
							layout="vertical"
							name="authorName"
							title={t("forms.gitlab-source-data.props.userName.name")}
						/>

						<FormField
							control={({ field }) =>
								isLoading ? (
									<Skeleton className="w-full" style={{ height: "36px" }} />
								) : (
									<TextInput
										{...field}
										placeholder={
											isLoading
												? t("loading")
												: t("forms.gitlab-source-data.props.userEmail.placeholder")
										}
									/>
								)
							}
							description={t("forms.gitlab-source-data.props.userEmail.description")}
							layout="vertical"
							name="authorEmail"
							title={t("forms.gitlab-source-data.props.userEmail.name")}
						/>
					</FormFieldSet>
				</FormStack>
			</form>
		</Form>
	);
};

export default EditGitLab;
